<template>
  <div class="paypal-return-page">
    <div class="container-fluid p-4">
      <div class="row justify-content-center">
        <div class="col-md-8 col-lg-6">
          <div class="card shadow-sm">
            <div class="card-body text-center">
              <div v-if="loading" class="py-5">
                <div class="spinner-border text-primary" role="status">
                  <span class="visually-hidden">Laden...</span>
                </div>
                <p class="mt-3">Verarbeite Zahlung...</p>
              </div>
              
              <div v-else-if="paymentStatus === 'success'" class="py-4">
                <div class="text-success mb-3">
                  <font-awesome-icon icon="check-circle" class="fa-3x" />
                </div>
                <h3 class="text-success mb-3">Zahlung erfolgreich!</h3>
                <p class="text-muted mb-4">
                  Die Zahlung wurde erfolgreich abgeschlossen. 
                  Du erhältst eine Bestätigung per E-Mail.
                </p>
                <div class="alert alert-info text-start">
                  <h6>Zahlungsdetails:</h6>
                  <div class="row">
                    <div class="col-6"><strong>Betrag:</strong></div>
                    <div class="col-6 text-end">{{ formatCurrency(paymentData.amount) }}</div>
                  </div>
                  <div class="row">
                    <div class="col-6"><strong>Status:</strong></div>
                    <div class="col-6 text-end">
                      <span class="badge bg-success">{{ paymentData.status }}</span>
                    </div>
                  </div>
                  <div class="row">
                    <div class="col-6"><strong>Transaktions-ID:</strong></div>
                    <div class="col-6 text-end">
                      <small class="text-muted">{{ paymentData.captureId }}</small>
                    </div>
                  </div>
                </div>
                <div class="d-flex gap-2 justify-content-center">
                  <router-link to="/dashboard" class="btn btn-primary">
                    <font-awesome-icon icon="home" class="me-2" />
                    Zum Dashboard
                  </router-link>
                  <button @click="createNewPayment" class="btn btn-outline-primary">
                    <font-awesome-icon icon="plus" class="me-2" />
                    Neue Zahlung
                  </button>
                </div>
              </div>
              
              <div v-else-if="paymentStatus === 'cancelled'" class="py-4">
                <div class="text-warning mb-3">
                  <font-awesome-icon icon="times-circle" class="fa-3x" />
                </div>
                <h3 class="text-warning mb-3">Zahlung abgebrochen</h3>
                <p class="text-muted mb-4">
                  Die Zahlung wurde abgebrochen. 
                  Du kannst jederzeit eine neue Zahlungsanfrage erstellen.
                </p>
                <div class="d-flex gap-2 justify-content-center">
                  <router-link to="/dashboard" class="btn btn-primary">
                    <font-awesome-icon icon="home" class="me-2" />
                    Zum Dashboard
                  </router-link>
                  <button @click="createNewPayment" class="btn btn-outline-primary">
                    <font-awesome-icon icon="plus" class="me-2" />
                    Neue Zahlung
                  </button>
                </div>
              </div>
              
              <div v-else-if="paymentStatus === 'error'" class="py-4">
                <div class="text-danger mb-3">
                  <font-awesome-icon icon="exclamation-triangle" class="fa-3x" />
                </div>
                <h3 class="text-danger mb-3">Fehler bei der Zahlung</h3>
                <p class="text-muted mb-4">
                  Es ist ein Fehler bei der Verarbeitung der Zahlung aufgetreten.
                </p>
                <div class="alert alert-danger text-start">
                  <strong>Fehler:</strong> {{ errorMessage }}
                </div>
                <div class="d-flex gap-2 justify-content-center">
                  <router-link to="/dashboard" class="btn btn-primary">
                    <font-awesome-icon icon="home" class="me-2" />
                    Zum Dashboard
                  </router-link>
                  <button @click="retryPayment" class="btn btn-outline-warning">
                    <font-awesome-icon icon="redo" class="me-2" />
                    Erneut versuchen
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { showError, showSuccess } from '@/services/notification'
import { paypalBusinessApi } from '@/api/paypalBusiness'
import { recheckPayPalPayment as recheckPayPalPaymentApi } from '@/api/billing'
import { teamBillingApi } from '@/api/teamBilling'

const route = useRoute()
const router = useRouter()

const loading = ref(true)
const paymentStatus = ref('')
const paymentData = ref({})
const errorMessage = ref('')

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(amount)
}

const handlePaymentReturn = async () => {
  try {
    const { success, billing, teamBilling } = route.query
    
    if (success === 'true') {
      let recheckResult
      
      if (billing) {
        recheckResult = await recheckPayPalPaymentApi(billing)
      } else if (teamBilling) {
        recheckResult = await teamBillingApi.recheckPayPalTeamPayment(teamBilling)
      } else {
        throw new Error('Keine Billing-ID gefunden')
      }
      
      if (recheckResult.status === 'completed') {
        paymentStatus.value = 'success'
        paymentData.value = {
          amount: recheckResult.billing?.price || recheckResult.teamBilling?.amount,
          status: 'completed',
          captureId: recheckResult.billing?.paypalOrderId || recheckResult.teamBilling?.paypalOrderId
        }
        showSuccess('Zahlung erfolgreich abgeschlossen!')
      } else if (recheckResult.status === 'pending') {
        paymentStatus.value = 'error'
        errorMessage.value = 'Zahlung ist noch ausstehend. Bitte versuch es später erneut.'
        showError('Zahlung noch nicht bestätigt')
      } else {
        paymentStatus.value = 'error'
        errorMessage.value = 'Zahlung fehlgeschlagen'
        showError('Zahlung fehlgeschlagen')
      }
    } else if (success === 'false') {
      paymentStatus.value = 'cancelled'
      showError('Zahlung wurde abgebrochen')
    } else {
      paymentStatus.value = 'error'
      errorMessage.value = 'Ungültige Rückkehr-Parameter'
      showError('Ungültige Zahlungsrückkehr')
    }
  } catch (error) {
    console.error('Payment return error:', error)
    paymentStatus.value = 'error'
    errorMessage.value = error.response?.data?.message || error.message || 'Unbekannter Fehler'
    showError('Fehler bei der Zahlungsverarbeitung')
  } finally {
    loading.value = false
  }
}

const createNewPayment = () => {
  router.push('/dashboard')
}

const retryPayment = () => {
  loading.value = true
  paymentStatus.value = ''
  paymentData.value = {}
  errorMessage.value = ''
  handlePaymentReturn()
}

onMounted(() => {
  handlePaymentReturn()
})
</script>
